/**
 * Helpers for the per-assessment payment flow:
 * which domains have been scored in the current paid assessment, and which
 * domain the clinician should go to next.
 *
 * Patient history (`/api/AnswerReport/GetHistoryList`) is grouped by day and
 * carries no SubmissionGroupId, so "scored in this assessment" means: a history
 * entry dated on or after the day the assessment was paid. Dates are compared as
 * ISO `YYYY-MM-DD` strings so browser time zones can't shift them (both values
 * come from the server in UTC).
 */

/** Display order. The DB `Category` table stays the source of truth for which domains exist. */
export const DISPLAY_ORDER = ['SE', 'PB', 'CS', 'RL', 'RC', 'EL', 'EC', 'GM', 'FM', 'ADL', 'SEN', 'BEH'];

export const orderOf = (code) => {
  const i = DISPLAY_ORDER.indexOf(code);
  return i === -1 ? DISPLAY_ORDER.length : i;
};

const dayOf = (value) => (value ? String(value).slice(0, 10) : '');

/**
 * Set of domain identifiers (names and/or codes, as history reports them)
 * scored in the given assessment. Without an assessment (exempt clinics) it
 * falls back to the most recent history entry.
 */
export const scoredDomainKeys = (history, assessment) => {
  const entries = Array.isArray(history) ? history : [];
  let relevant;

  const since = dayOf(assessment?.paidAt || assessment?.createdAt);
  if (since) {
    relevant = entries.filter((h) => dayOf(h.submittedAt) >= since);
  } else {
    const latest = [...entries].sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))[0];
    relevant = latest ? [latest] : [];
  }

  const keys = new Set();
  relevant.forEach((h) => (h.domains || []).forEach((d) => { if (d.domain) keys.add(d.domain); }));
  return keys;
};

export const isDomainScored = (keys, category) =>
  keys.has(category.name) || keys.has(category.code);

/** First domain (in display order) not yet scored, excluding `currentCode`; null when none left. */
export const nextUnscoredDomain = (categories, keys, currentCode) =>
  [...(categories || [])]
    .sort((a, b) => orderOf(a.code) - orderOf(b.code))
    .find((c) => c.code !== currentCode && !isDomainScored(keys, c)) || null;
